import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Building2, User, CreditCard, DollarSign, Calendar, Mail, Phone, Edit2, Save, X, RefreshCw, Power, AlertTriangle } from 'lucide-react';
import { formatDateUTC } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to determine currency based on country
const getCurrencyForCountry = (country) => {
  const countryLower = (country || '').toLowerCase().trim();
  if (countryLower.includes('colombia')) return 'COP';
  if (countryLower.includes('chile')) return 'CLP';
  return 'USD';
};

// Format currency helper
const formatCurrency = (amount, currency = 'COP') => {
  const formatted = new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
  return `$${formatted} ${currency}`;
};

// Get price for specific currency from membership type
const getPriceForCurrency = (membershipType, currencyCode) => {
  if (!membershipType) return 0;
  const precios = membershipType.precios || [];
  const precio = precios.find(p => p.currency_code === currencyCode);
  if (precio) return precio.amount;
  return membershipType.valor || 0; // Fallback to legacy field
};

export default function AdminMembershipDetalle() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [membershipTypes, setMembershipTypes] = useState([]);
  
  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [showChangeMembershipModal, setShowChangeMembershipModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deactivating, setDeactivating] = useState(false);
  
  // Form states
  const [paymentData, setPaymentData] = useState({
    valor_pagado: 0,
    moneda: 'COP',
    medio_pago: 'Transferencia',
    fecha_pago: new Date().toISOString().split('T')[0],
    tipo_membresia: ''
  });
  
  const [newMembershipData, setNewMembershipData] = useState({
    membership_type_id: '',
    fecha_activacion: new Date().toISOString().split('T')[0],
    valor: 0
  });
  
  const [userEditData, setUserEditData] = useState({
    nombre_completo: '',
    email: '',
    celular: '',
    username: '',
    new_password: ''
  });

  // Company currency based on country
  const companyCurrency = data?.company ? getCurrencyForCountry(data.company.pais) : 'COP';

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const [companyRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/admin/companies/${companyId}/detail`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/membership-types`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setData(companyRes.data);
      setMembershipTypes(typesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
      navigate('/admin/memberships');
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditUser = (user) => {
    setUserEditData({
      nombre_completo: user.nombre_completo,
      email: user.email,
      celular: user.celular,
      username: user.username,
      new_password: ''
    });
    setEditingUser(user.id);
  };

  const handleSaveUser = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.put(`${API_URL}/admin/users/${editingUser}`, userEditData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Usuario actualizado correctamente');
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handlePayment = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.post(`${API_URL}/admin/payments`, {
        company_id: companyId,
        membership_id: data.active_membership?.id,
        ...paymentData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pago registrado exitosamente');
      setShowPaymentModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const handleCreateMembership = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.post(`${API_URL}/admin/memberships`, {
        company_id: companyId,
        membership_type_id: newMembershipData.membership_type_id,
        fecha_activacion: newMembershipData.fecha_activacion,
        valor: newMembershipData.valor,
        moneda: companyCurrency
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membresía activada exitosamente');
      setShowMembershipModal(false);
      setShowChangeMembershipModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear membresía');
    }
  };

  const handleDeactivateMembership = async () => {
    if (!data.active_membership) return;
    
    setDeactivating(true);
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.put(`${API_URL}/admin/memberships/${data.active_membership.id}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membresía desactivada. La empresa ya no podrá acceder a las funciones del sistema.');
      setShowDeactivateModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar membresía');
    } finally {
      setDeactivating(false);
    }
  };

  const handleMembershipTypeChange = (typeId, isForChange = false) => {
    const selectedType = membershipTypes.find(t => t.id === typeId);
    const price = getPriceForCurrency(selectedType, companyCurrency);
    
    if (isForChange) {
      setNewMembershipData({
        ...newMembershipData,
        membership_type_id: typeId,
        valor: price
      });
    } else {
      setNewMembershipData({
        ...newMembershipData,
        membership_type_id: typeId,
        valor: price
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { company, users, active_membership } = data;
  const adminUser = users.find(u => u.role === 'admin') || users[0];
  
  // Calculate consent stats
  const consentLimit = active_membership?.cantidad_consentimientos || 0;
  const consentsUsed = active_membership?.consentimientos_usados || 0;
  const consentsRemaining = consentLimit === -1 ? '∞' : Math.max(0, consentLimit - consentsUsed);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-membership-detalle">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin/memberships')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Detalle de Membresía</h1>
            <p className="text-slate-600">{company.razon_social || company.nombre_fantasia}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* Datos de la Empresa */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <Building2 className="text-violet-600" size={24} />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Datos de la Empresa</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500 mb-1">Código de Empresa</p>
              <p className="font-mono font-bold text-violet-600 text-lg">{company.codigo_empresa}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Razón Social</p>
              <p className="font-semibold text-slate-900">{company.razon_social || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-1">Número de Documento</p>
              <p className="font-mono font-semibold text-slate-900">{company.numero_documento || 'N/A'}</p>
            </div>
          </div>
        </section>

        {/* Datos del Usuario Administrador */}
        {adminUser && (
          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <User className="text-blue-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Usuario Administrador</h2>
              </div>
              {editingUser !== adminUser.id ? (
                <button 
                  onClick={() => handleStartEditUser(adminUser)}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 hover:bg-violet-100 rounded-lg font-semibold transition-colors"
                >
                  <Edit2 size={18} />
                  Editar
                </button>
              ) : (
                <div className="flex gap-2">
                  <button 
                    onClick={() => setEditingUser(null)}
                    className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg font-semibold transition-colors"
                  >
                    <X size={18} />
                    Cancelar
                  </button>
                  <button 
                    onClick={handleSaveUser}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg font-semibold transition-colors"
                  >
                    <Save size={18} />
                    Guardar
                  </button>
                </div>
              )}
            </div>
            
            {editingUser === adminUser.id ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nombre Completo</label>
                  <input 
                    type="text" 
                    value={userEditData.nombre_completo} 
                    onChange={(e) => setUserEditData({...userEditData, nombre_completo: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Usuario</label>
                  <input 
                    type="text" 
                    value={userEditData.username} 
                    onChange={(e) => setUserEditData({...userEditData, username: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input 
                    type="email" 
                    value={userEditData.email} 
                    onChange={(e) => setUserEditData({...userEditData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Celular</label>
                  <input 
                    type="text" 
                    value={userEditData.celular} 
                    onChange={(e) => setUserEditData({...userEditData, celular: e.target.value})}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Nueva Contraseña (dejar vacío para no cambiar)</label>
                  <input 
                    type="password" 
                    value={userEditData.new_password} 
                    onChange={(e) => setUserEditData({...userEditData, new_password: e.target.value})}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Nombre Completo</p>
                  <p className="font-semibold text-slate-900">{adminUser.nombre_completo}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Usuario</p>
                  <p className="font-mono font-semibold text-violet-600">{adminUser.username}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Email</p>
                  <p className="font-semibold text-slate-900 flex items-center gap-1">
                    <Mail size={14} className="text-slate-400" />
                    {adminUser.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Celular</p>
                  <p className="font-semibold text-slate-900 flex items-center gap-1">
                    <Phone size={14} className="text-slate-400" />
                    {adminUser.celular}
                  </p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Membresía */}
        {active_membership ? (
          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="text-amber-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-slate-900">Membresía Activa</h2>
              </div>
              <div className="flex flex-wrap gap-2">
                <button 
                  onClick={() => setShowDeactivateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg font-semibold transition-colors"
                  data-testid="btn-deactivate-membership"
                >
                  <Power size={18} />
                  Desactivar
                </button>
                <button 
                  onClick={() => {
                    setNewMembershipData({
                      membership_type_id: '',
                      fecha_activacion: new Date().toISOString().split('T')[0],
                      valor: 0
                    });
                    setShowChangeMembershipModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-600 hover:bg-amber-100 rounded-lg font-semibold transition-colors"
                >
                  <RefreshCw size={18} />
                  Cambiar Membresía
                </button>
                <button 
                  onClick={() => {
                    const selectedType = membershipTypes.find(t => t.id === active_membership.membership_type_id);
                    const price = active_membership.valor || getPriceForCurrency(selectedType, companyCurrency);
                    const currency = active_membership.moneda || companyCurrency;
                    setPaymentData({
                      valor_pagado: price,
                      moneda: currency,
                      medio_pago: 'Transferencia',
                      fecha_pago: new Date().toISOString().split('T')[0],
                      tipo_membresia: active_membership.tipo_membresia || ''
                    });
                    setShowPaymentModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg font-semibold transition-colors"
                >
                  <DollarSign size={18} />
                  Realizar Pago
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-violet-50 rounded-xl p-4">
                <p className="text-sm text-violet-600 mb-1">Membresía Actual</p>
                <p className="text-2xl font-bold text-violet-700">{active_membership.tipo_membresia || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Fecha de Activación</p>
                <p className="font-semibold text-slate-900 flex items-center gap-1">
                  <Calendar size={14} className="text-slate-400" />
                  {formatDateUTC(active_membership.fecha_activacion)}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Fecha de Renovación</p>
                <p className="font-semibold text-slate-900 flex items-center gap-1">
                  <Calendar size={14} className="text-slate-400" />
                  {formatDateUTC(active_membership.fecha_renovacion)}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Valor</p>
                <p className="text-xl font-bold text-emerald-600">
                  {formatCurrency(active_membership.valor || getPriceForCurrency(membershipTypes.find(t => t.id === active_membership.membership_type_id), companyCurrency), active_membership.moneda || companyCurrency)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-slate-200">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-sm text-blue-600 mb-1">Consentimientos Creados</p>
                <p className="text-3xl font-bold text-blue-700">{consentsUsed}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-sm text-emerald-600 mb-1">Consentimientos Disponibles</p>
                <p className="text-3xl font-bold text-emerald-700">{consentsRemaining}</p>
              </div>
              <div className={`rounded-xl p-4 text-center ${active_membership.estado_pago === 'Vigente' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                <p className={`text-sm mb-1 ${active_membership.estado_pago === 'Vigente' ? 'text-emerald-600' : 'text-amber-600'}`}>Estado de Pago</p>
                <p className={`text-2xl font-bold ${active_membership.estado_pago === 'Vigente' ? 'text-emerald-700' : 'text-amber-700'}`}>
                  {active_membership.estado_pago || 'N/A'}
                </p>
              </div>
            </div>

            {/* Toggle Código de Firma (manual override) */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                <div>
                  <p className="font-semibold text-slate-900">Código de Firma</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Habilitar firma por código presencial para este centro (override manual)
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={active_membership.codigo_firma_habilitado || false}
                    onChange={async (e) => {
                      try {
                        const token = localStorage.getItem('konemed_admin_token');
                        await axios.put(`${API_URL}/admin/memberships/${active_membership.id}/toggle-codigo-firma`, 
                          { habilitado: e.target.checked },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        toast.success(e.target.checked ? 'Código de firma habilitado' : 'Código de firma deshabilitado');
                        fetchData();
                      } catch (error) {
                        toast.error('Error al actualizar');
                      }
                    }}
                    data-testid="toggle-codigo-firma-manual"
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                </label>
              </div>
            </div>
          </section>
        ) : (
          <section className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <CreditCard className="text-slate-400" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Sin Membresía Activa</h2>
                  <p className="text-slate-500">Esta empresa no tiene una membresía activa</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setNewMembershipData({
                    membership_type_id: membershipTypes[0]?.id || '',
                    fecha_activacion: new Date().toISOString().split('T')[0],
                    valor: membershipTypes[0]?.valor || 0
                  });
                  setShowMembershipModal(true);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-violet-600 text-white hover:bg-violet-700 rounded-lg font-semibold transition-colors"
              >
                <CreditCard size={20} />
                Activar Membresía
              </button>
            </div>
          </section>
        )}
      </main>

      {/* Modal Realizar Pago */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Registrar Pago</h2>
            <p className="text-slate-600 mb-2">Empresa: <strong>{company.razon_social}</strong></p>
            <p className="text-sm text-violet-600 mb-6 bg-violet-50 px-3 py-2 rounded-lg">
              Moneda: <strong>{paymentData.moneda}</strong> (según país: {company.pais || 'N/A'})
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Valor a Pagar ({paymentData.moneda})</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400">$</span>
                  <input 
                    type="number" 
                    value={paymentData.valor_pagado} 
                    onChange={(e) => setPaymentData({...paymentData, valor_pagado: parseFloat(e.target.value)})}
                    className="w-full pl-8 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <p className="text-sm text-emerald-600 mt-1 font-semibold">
                  {formatCurrency(paymentData.valor_pagado, paymentData.moneda)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Medio de Pago</label>
                <select 
                  value={paymentData.medio_pago} 
                  onChange={(e) => setPaymentData({...paymentData, medio_pago: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="Transferencia">Transferencia</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Pago Online">Pago Online</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha de Pago</label>
                <input 
                  type="date" 
                  value={paymentData.fecha_pago} 
                  onChange={(e) => setPaymentData({...paymentData, fecha_pago: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de Membresía</label>
                <input 
                  type="text" 
                  value={paymentData.tipo_membresia} 
                  onChange={(e) => setPaymentData({...paymentData, tipo_membresia: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                  readOnly
                />
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button 
                onClick={() => setShowPaymentModal(false)} 
                className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handlePayment} 
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors"
              >
                <DollarSign size={20} />
                Registrar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Activar Membresía (sin membresía activa) */}
      {showMembershipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Activar Membresía</h2>
            <p className="text-slate-600 mb-2">Empresa: <strong>{company.razon_social}</strong></p>
            <p className="text-sm text-violet-600 mb-6 bg-violet-50 px-3 py-2 rounded-lg">
              País: <strong>{company.pais || 'N/A'}</strong> → Moneda: <strong>{companyCurrency}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha de Activación</label>
                <input 
                  type="date" 
                  value={newMembershipData.fecha_activacion} 
                  onChange={(e) => setNewMembershipData({...newMembershipData, fecha_activacion: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Tipo de Membresía</label>
                <select 
                  value={newMembershipData.membership_type_id} 
                  onChange={(e) => handleMembershipTypeChange(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="">Seleccione...</option>
                  {membershipTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.nombre} - {type.cantidad_consentimientos === -1 ? 'Ilimitado' : `${type.cantidad_consentimientos} cons.`} - {formatCurrency(getPriceForCurrency(type, companyCurrency), companyCurrency)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Valor de la Membresía ({companyCurrency})</label>
                <input 
                  type="number" 
                  value={newMembershipData.valor} 
                  onChange={(e) => setNewMembershipData({...newMembershipData, valor: parseFloat(e.target.value)})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-sm text-emerald-600 mt-1 font-semibold">
                  {formatCurrency(newMembershipData.valor, companyCurrency)}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button 
                onClick={() => setShowMembershipModal(false)} 
                className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleCreateMembership}
                disabled={!newMembershipData.membership_type_id}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                <CreditCard size={20} />
                Activar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cambiar Membresía */}
      {showChangeMembershipModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Cambiar Membresía</h2>
            <p className="text-slate-600 mb-2">Empresa: <strong>{company.razon_social}</strong></p>
            <p className="text-amber-600 text-sm mb-2 bg-amber-50 p-3 rounded-lg">
              Al cambiar la membresía, se activará una nueva y la fecha de cobro correrá desde hoy.
            </p>
            <p className="text-sm text-violet-600 mb-6 bg-violet-50 px-3 py-2 rounded-lg">
              País: <strong>{company.pais || 'N/A'}</strong> → Moneda: <strong>{companyCurrency}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha de Activación</label>
                <input 
                  type="date" 
                  value={newMembershipData.fecha_activacion} 
                  onChange={(e) => setNewMembershipData({...newMembershipData, fecha_activacion: e.target.value})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nuevo Tipo de Membresía</label>
                <select 
                  value={newMembershipData.membership_type_id} 
                  onChange={(e) => handleMembershipTypeChange(e.target.value, true)}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                >
                  <option value="">Seleccione...</option>
                  {membershipTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.nombre} - {type.cantidad_consentimientos === -1 ? 'Ilimitado' : `${type.cantidad_consentimientos} cons.`} - {formatCurrency(getPriceForCurrency(type, companyCurrency), companyCurrency)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Valor de la Membresía ({companyCurrency})</label>
                <input 
                  type="number" 
                  value={newMembershipData.valor} 
                  onChange={(e) => setNewMembershipData({...newMembershipData, valor: parseFloat(e.target.value)})}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <p className="text-sm text-emerald-600 mt-1 font-semibold">
                  {formatCurrency(newMembershipData.valor, companyCurrency)}
                </p>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button 
                onClick={() => setShowChangeMembershipModal(false)} 
                className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleCreateMembership}
                disabled={!newMembershipData.membership_type_id}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={20} />
                Cambiar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Desactivar Membresía */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Desactivar Membresía</h2>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-800 font-medium mb-2">¿Está seguro de desactivar esta membresía?</p>
              <p className="text-red-700 text-sm">
                Al desactivar la membresía, la empresa <strong>{company.razon_social}</strong> no podrá:
              </p>
              <ul className="text-red-700 text-sm mt-2 space-y-1 list-disc list-inside">
                <li>Crear nuevos consentimientos</li>
                <li>Ver consentimientos pendientes</li>
                <li>Ver consentimientos firmados</li>
                <li>Gestionar usuarios</li>
                <li>Configurar consentimientos</li>
              </ul>
              <p className="text-red-700 text-sm mt-3">
                Solo podrán ver el Dashboard con un mensaje de membresía desactivada.
              </p>
            </div>

            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-slate-600 mb-2"><strong>Membresía actual:</strong> {active_membership?.tipo_membresia}</p>
              <p className="text-sm text-slate-600"><strong>Consentimientos usados:</strong> {active_membership?.consentimientos_usados || 0}</p>
            </div>
            
            <div className="flex gap-4">
              <button 
                onClick={() => setShowDeactivateModal(false)} 
                className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button 
                onClick={handleDeactivateMembership}
                disabled={deactivating}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {deactivating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Desactivando...
                  </>
                ) : (
                  <>
                    <Power size={20} />
                    Desactivar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
